import { useMemo } from 'react';
import { useNodeTranscript } from './useNodeTranscript';
import type { ProcessNode } from '../graphTypes';
import type { NodeLiveState, TranscriptEntry } from '../runSession/types';

interface PersistedExecution {
  readonly status?: string;
  readonly errorMessage?: string | null;
  readonly errorDetail?: string | null;
}

export interface NodeFailureCopy {
  readonly message: string | null;
  readonly detail: string | null;
}

export interface SelectedNodeDock {
  readonly liveState: NodeLiveState | undefined;
  readonly transcript: readonly TranscriptEntry[] | undefined;
  readonly failure: NodeFailureCopy | null;
}

export interface SelectedNodeDockParams {
  readonly processId: string | undefined;
  readonly selectedNodeId: string | null;
  readonly nodes: readonly ProcessNode[];
  readonly runId: string | null;
  readonly liveStateForNode: (nodeId: string) => NodeLiveState | undefined;
}

/**
 * Everything the dock shows about the selected node: its live state, the
 * transcript to display, and the failure copy.
 */
export function useSelectedNodeDock({
  processId,
  selectedNodeId,
  nodes,
  runId,
  liveStateForNode,
}: SelectedNodeDockParams): SelectedNodeDock {
  const liveState = selectedNodeId ? liveStateForNode(selectedNodeId) : undefined;
  const persisted = selectedNodeId ? persistedExecution(nodes, selectedNodeId) : undefined;
  const status = liveState ? liveState.status : persisted?.status;
  const isTerminal = status === 'failed' || status === 'completed';

  // Gate the persisted-transcript fetch on a KNOWN terminal node state --
  // live (this session) or the persisted snapshot (after a reload). Nodes
  // that never ran have neither, so we still avoid the guaranteed-404 fetch.
  const persistedTranscript = useNodeTranscript(
    processId,
    runId ?? undefined,
    selectedNodeId ?? undefined,
    liveState?.iteration ?? 0,
    !!selectedNodeId && !!runId && isTerminal,
  );

  // Terminal nodes prefer the persisted transcript: the live one can be a
  // post-reconnect tail (SSE replays best-effort, capped by the stream
  // MAXLEN), while the persisted row is written complete when the node
  // finishes. Live entries cover running nodes and the window while the
  // persisted fetch is in flight.
  const transcript = useMemo(() => {
    if (!selectedNodeId) return undefined;
    if (isTerminal && persistedTranscript) return persistedTranscript;
    return liveState?.transcript;
  }, [selectedNodeId, isTerminal, liveState?.transcript, persistedTranscript]);

  return {
    liveState,
    transcript,
    failure: selectedNodeId ? failureCopy(liveState, persisted) : null,
  };
}

function persistedExecution(
  nodes: readonly ProcessNode[],
  nodeId: string,
): PersistedExecution | undefined {
  const data = nodes.find((n) => n.id === nodeId)?.data as
    | { latestExecution?: PersistedExecution | null }
    | undefined;
  return data?.latestExecution ?? undefined;
}

/**
 * Live state wins while it exists -- its null errorMessage means "no error
 * THIS run"; the persisted snapshot only fills in after a reload.
 */
function failureCopy(
  live: NodeLiveState | undefined,
  persisted: PersistedExecution | undefined,
): NodeFailureCopy {
  if (live) {
    return { message: live.errorMessage, detail: live.errorDetail };
  }
  return {
    message: persisted?.errorMessage ?? null,
    detail: persisted?.errorDetail ?? null,
  };
}
